import React from 'react';
import { House, X } from 'lucide-react';
import type { Theme } from '../../types';
import { resolveThemeToken } from '../../utils/theme';
import { isValidImageUrl } from '../../utils/fileValidation';
const ExportMenu = React.lazy(() => import(/* webpackChunkName: "export-menu" */ './ExportMenu'));
import ViewTabs from '../ViewTabs';
import type { WidgetStrings } from '../../utils/i18n';
import { DEFAULT_WIDGET_STRINGS, formatWidgetString } from '../../utils/i18n';

interface HeaderProps {
  view: string;
  viewCounts: Record<string, number>;
  theme: Theme;
  companyName: string;
  companyLogo: string;
  agentName: string;
  agentAvatar: string;
  titleId?: string;
  tabsIdBase: string;
  showViewTabs: boolean;
  visibleViews?: string[];
  offline?: boolean;
  showSessionsButton?: boolean;
  onSessionsClick?: () => void;
  onViewChange: (view: string) => void;
  onMinimize: () => void;
  onHome: () => void;
  onExport: (format: string) => void;
  onForget?: () => void;
  settingsConsoleUrl?: string;
  settingsConsoleLabel?: string;
  strings?: WidgetStrings;
  /** Render the (lazy) export menu; false skips loading its chunk (default true). */
  showExportMenu?: boolean;
}

const Header = React.memo(function Header({
  view,
  viewCounts,
  companyName,
  companyLogo,
  agentName,
  agentAvatar,
  theme,
  titleId,
  tabsIdBase,
  showViewTabs,
  visibleViews,
  offline = false,
  showSessionsButton,
  onSessionsClick,
  onViewChange,
  onMinimize,
  onHome,
  onExport,
  onForget,
  settingsConsoleUrl,
  settingsConsoleLabel,
  strings = DEFAULT_WIDGET_STRINGS,
  showExportMenu = true,
}: HeaderProps) {
  const headerBackground = resolveThemeToken(theme?.header || 'bg-white', 'backgroundColor');
  // Text and background must come from the same palette: dark mode sets --rw-text to a light
  // colour while theme.header may stay a light brand colour, which would fail contrast.
  const headerText = resolveThemeToken(theme?.text, 'color');
  return (
    <div
      className={`rw-header border-b ${headerBackground.className} ${headerText.className}`.trim()}
      style={{ ...headerBackground.style, ...headerText.style }}
    >
      <div className="rw-header-inner flex items-center justify-between px-4 py-3">
        <div className="rw-header-identity flex items-center gap-3">
          {agentAvatar && isValidImageUrl(agentAvatar) && (
            <img
              src={agentAvatar}
              alt={`${agentName} avatar`}
              className="w-8 h-8 rounded-full border border-gray-200 object-cover"
              loading="lazy"
              decoding="async"
              onError={e => {
                (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
              }}
            />
          )}
          <div className="rw-header-copy leading-tight">
            <h2 className="rw-header-title text-sm font-semibold" id={titleId}>
              {companyLogo && isValidImageUrl(companyLogo) ? (
                <>
                  <img
                    src={companyLogo}
                    alt={`${companyName} logo`}
                    className="h-3 w-auto max-w-[90px] object-contain"
                    loading="lazy"
                    decoding="async"
                    onError={e => {
                      (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                    }}
                  />
                  <span className="sr-only">{agentName}</span>
                </>
              ) : (
                agentName
              )}
            </h2>
            <div className="rw-header-status text-xs opacity-90">
              {offline
                ? formatWidgetString(strings.offlineWithCompany, { company: companyName })
                : formatWidgetString(strings.onlineWithCompany, { company: companyName })}
            </div>
          </div>
        </div>
        <div className="rw-header-actions flex items-center gap-2">
          <button
            onClick={onHome}
            type="button"
            className="inline-flex items-center gap-1 px-2 py-1 rounded hover:opacity-80 focus:outline-2 focus:outline-blue-500"
            title={strings.home}
            aria-label={strings.home}
          >
            <House className="w-3.5 h-3.5" aria-hidden="true" />
            <span className="rw-header-action-label text-xs">{strings.home}</span>
          </button>
          {showExportMenu && (
            <React.Suspense fallback={null}>
              <ExportMenu
                onExport={onExport}
                onSessionsClick={showSessionsButton ? onSessionsClick : undefined}
                onForget={onForget}
                settingsConsoleUrl={settingsConsoleUrl}
                settingsConsoleLabel={settingsConsoleLabel}
                strings={strings}
              />
            </React.Suspense>
          )}
          <button
            onClick={onMinimize}
            type="button"
            className="p-1 rounded hover:opacity-80 focus:outline-2 focus:outline-blue-500"
            title={strings.minimizeChat}
            aria-label={strings.minimizeChat}
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </div>
      {showViewTabs ? (
        <ViewTabs
          view={view}
          viewCounts={viewCounts}
          visibleViews={visibleViews}
          onChange={onViewChange}
          idBase={tabsIdBase}
          strings={strings}
        />
      ) : null}
    </div>
  );
});

export default Header;
